// src/pages/StaffProfileTab.jsx
import React from "react";
import axios from "../api/axios";
import "./StaffProfileTab.css";

const cleanPatch = (obj) => {
  // убираем null/undefined и (опционально) пустые строки
  const out = {};
  Object.entries(obj || {}).forEach(([k, v]) => {
    if (v === null || v === undefined) return;
    // если хочешь НЕ сохранять пустые строки, раскомментируй:
    // if (typeof v === "string" && v.trim() === "") return;
    out[k] = v;
  });
  return out;
};

export default function StaffProfileTab({
  locale = "kz",
  profile,
  rolesLower = [],
  roleNames = {},
  departmentNames = {},
  onProfilePatch,
}) {
  const [isEditing, setIsEditing] = React.useState(false);
  const [message, setMessage] = React.useState("");
  const [showPwdModal, setShowPwdModal] = React.useState(false);

  // редактируем все поля, которые разрешены бэком
  const [editData, setEditData] = React.useState({
    name: "",
    username: "",
    email: "",
    phone: "",
    position: "",
    address: "",
    document_number: "",
    iin: "",
    iik: "",
    bank_name: "",
    academic_degree: "",
  });

  const [passwordData, setPasswordData] = React.useState({
    current_password: "",
    new_password: "",
    new_password_confirmation: "",
  });

  // sync edit form when profile changes
  React.useEffect(() => {
    setEditData({
      name: profile?.name || "",
      username: profile?.username || "",
      email: profile?.email || "",
      phone: profile?.phone || "",
      position: profile?.position || "",
      address: profile?.address || "",
      document_number: profile?.document_number || "",
      iin: profile?.iin || "",
      iik: profile?.iik || "",
      bank_name: profile?.bank_name || "",
      academic_degree: profile?.academic_degree || "",
    });
    setMessage("");
    setIsEditing(false);
  }, [profile?.id]);

const rolesList = React.useMemo(() => {
  const roles = Array.isArray(profile?.roles) && profile.roles.length
    ? profile.roles
    : (Array.isArray(rolesLower) ? rolesLower : []);

  if (!roles.length) return [];

  return roles
    .filter(Boolean)
    .map((r) => {
      // если roleNames хранит по "slug" (director/external_expert) — покажем перевод
      const label =
        roleNames?.[r]?.[locale] ||
        roleNames?.[r]?.ru ||
        roleNames?.[r]?.kz ||
        r;

      return { key: r, label };
    });
}, [profile?.roles, rolesLower, roleNames, locale]);


  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setMessage("");

    try {
      const payload = cleanPatch(editData);

      const res = await axios.put("/internal/profile", payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      // бэк возвращает { message, user }
      const updated = res?.data?.user ? res.data.user : payload;

      onProfilePatch?.(updated);
      setIsEditing(false);

      setMessage(
        locale === "kz" ? "Профиль сәтті жаңартылды!" : "Профиль успешно обновлен!"
      );
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        (locale === "kz" ? "Қате орын алды." : "Произошла ошибка.");
      setMessage(msg);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setMessage("");

    try {
      await axios.put(
        "/internal/profile/change-password",
        {
          current_password: passwordData.current_password,
          new_password: passwordData.new_password,
          new_password_confirmation: passwordData.new_password_confirmation,
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );

      setPasswordData({
        current_password: "",
        new_password: "",
        new_password_confirmation: "",
      });

      setMessage(locale === "kz" ? "Құпиясөз сәтті өзгертілді!" : "Пароль успешно изменен!");
      setShowPwdModal(false);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        (locale === "kz" ? "Қате орын алды." : "Произошла ошибка.");
      setMessage(msg);
    }
  };

  return (
    <section className="dashboard-body">
      <div className="pd-profile">
        <section className="pd-prof-left">
          <div className="pd-card pd-card--profile">
            <div className="pd-prof-head">
              <div className="pd-prof-id">
                <div className="pd-avatar">
                  {(profile?.name || "U").slice(0, 2).toUpperCase()}
                </div>

                <div>
                  <h3 className="pd-prof-title">
                    {locale === "kz" ? "Профиль ақпараттары" : "Информация профиля"}
                  </h3>

                </div>
              </div>

              {!isEditing && (
                <div className="pd-actions">
                  <button className="pd-btn pd-btn--sm" type="button" onClick={() => setIsEditing(true)}>
                    {locale === "kz" ? "Өңдеу" : "Изменить"}
                  </button>

                  <button
                    className="pd-btn pd-btn--ghost pd-btn--sm"
                    type="button"
                    onClick={() => setShowPwdModal(true)}
                  >
                    {locale === "kz" ? "Құпиясөзді өзгерту" : "Сменить пароль"}
                  </button>
                </div>
              )}
            </div>

            {profile ? (
              isEditing ? (
                <form onSubmit={handleProfileUpdate} className="pd-formgrid">
                  {/* основное */}
                  <div className="pd-formgroup">
                    <label>{locale === "kz" ? "Аты-жөні" : "ФИО"}</label>
                    <input
                      type="text"
                      value={editData.name}
                      onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                      required
                    />
                  </div>

                  <div className="pd-formgroup">
                    <label>Username</label>
                    <input
                      type="text"
                      value={editData.username}
                      onChange={(e) => setEditData({ ...editData, username: e.target.value })}
                      required
                    />
                  </div>

                  <div className="pd-formgroup">
                    <label>Email</label>
                    <input
                      type="email"
                      value={editData.email}
                      onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                    />
                  </div>

                  {/* доп. поля */}
                  <div className="pd-formgroup">
                    <label>{locale === "kz" ? "Телефон" : "Телефон"}</label>
                    <input
                      type="text"
                      value={editData.phone}
                      onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                      placeholder="+7..."
                    />
                  </div>

                  <div className="pd-formgroup">
                    <label>{locale === "kz" ? "Лауазымы" : "Должность"}</label>
                    <input
                      type="text"
                      value={editData.position}
                      onChange={(e) => setEditData({ ...editData, position: e.target.value })}
                    />
                  </div>

                  <div className="pd-formgroup">
                    <label>{locale === "kz" ? "Мекенжай" : "Адрес"}</label>
                    <input
                      type="text"
                      value={editData.address}
                      onChange={(e) => setEditData({ ...editData, address: e.target.value })}
                    />
                  </div>

                  <div className="pd-formgroup">
                    <label>{locale === "kz" ? "Құжат №" : "Документ №"}</label>
                    <input
                      type="text"
                      value={editData.document_number}
                      onChange={(e) => setEditData({ ...editData, document_number: e.target.value })}
                    />
                  </div>

                  <div className="pd-formgroup">
                    <label>IIN</label>
                    <input
                      type="text"
                      value={editData.iin}
                      onChange={(e) => setEditData({ ...editData, iin: e.target.value })}
                      maxLength={12}
                    />
                  </div>

                  <div className="pd-formgroup">
                    <label>IIK</label>
                    <input
                      type="text"
                      value={editData.iik}
                      onChange={(e) => setEditData({ ...editData, iik: e.target.value })}
                      maxLength={34}
                    />
                  </div>

                  <div className="pd-formgroup">
                    <label>{locale === "kz" ? "Банк" : "Банк"}</label>
                    <input
                      type="text"
                      value={editData.bank_name}
                      onChange={(e) => setEditData({ ...editData, bank_name: e.target.value })}
                    />
                  </div>

                  <div className="pd-formgroup">
                    <label>{locale === "kz" ? "Ғылыми дәреже" : "Ученая степень"}</label>
                    <input
                      type="text"
                      value={editData.academic_degree}
                      onChange={(e) => setEditData({ ...editData, academic_degree: e.target.value })}
                    />
                  </div>

                  <div className="pd-row">
                    <button type="submit" className="pd-btn">
                      {locale === "kz" ? "Сақтау" : "Сохранить"}
                    </button>

                    <button
                      type="button"
                      className="pd-btn pd-btn--ghost"
                      onClick={() => {
                        setIsEditing(false);
                        setEditData({
                          name: profile?.name || "",
                          username: profile?.username || "",
                          email: profile?.email || "",
                          phone: profile?.phone || "",
                          position: profile?.position || "",
                          address: profile?.address || "",
                          document_number: profile?.document_number || "",
                          iin: profile?.iin || "",
                          iik: profile?.iik || "",
                          bank_name: profile?.bank_name || "",
                          academic_degree: profile?.academic_degree || "",
                        });
                        setMessage("");
                      }}
                    >
                      {locale === "kz" ? "Болдырмау" : "Отмена"}
                    </button>
                  </div>

                  {message && <p className="pd-success">{message}</p>}
                </form>
              ) : (
                <div className="pd-kv">


                  <div className="pd-term">{locale === "kz" ? "Аты-жөні" : "ФИО"}</div>
                  <div className="pd-val">{profile?.name || "-"}</div>

                  <div className="pd-term">Username</div>
                  <div className="pd-val">{profile?.username || "-"}</div>

                  <div className="pd-term">Email</div>
                  <div className="pd-val">{profile?.email || "-"}</div>

                  <div className="pd-term">{locale === "kz" ? "Телефон" : "Телефон"}</div>
                  <div className="pd-val">{profile?.phone || "-"}</div>

                  <div className="pd-term">{locale === "kz" ? "Лауазымы" : "Должность"}</div>
                  <div className="pd-val">{profile?.position || "-"}</div>

                  <div className="pd-term">{locale === "kz" ? "Мекенжай" : "Адрес"}</div>
                  <div className="pd-val">{profile?.address || "-"}</div>

                  <div className="pd-term">{locale === "kz" ? "Құжат №" : "Документ №"}</div>
                  <div className="pd-val">{profile?.document_number || "-"}</div>

                  <div className="pd-term">IIN</div>
                  <div className="pd-val">{profile?.iin || "-"}</div>

                  <div className="pd-term">IIK</div>
                  <div className="pd-val">{profile?.iik || "-"}</div>

                  <div className="pd-term">{locale === "kz" ? "Банк" : "Банк"}</div>
                  <div className="pd-val">{profile?.bank_name || "-"}</div>

                  <div className="pd-term">{locale === "kz" ? "Ғылыми дәреже" : "Ученая степень"}</div>
                  <div className="pd-val">{profile?.academic_degree || "-"}</div>

                  <div className="pd-term">{locale === "kz" ? "Бөлім" : "Отдел"}</div>
                  <div className="pd-val">
                    {profile?.department?.name || departmentNames[profile?.department_id]?.[locale] || "-"}
                  </div>

                  <div className="pd-term">{locale === "kz" ? "Күйі" : "Статус"}</div>
                  <div className="pd-val">
                    <span className="pd-pill pd-pill--muted">{profile?.status || "-"}</span>
                  </div>
                </div>
              )
            ) : (
              <p className="pd-muted">{locale === "kz" ? "Жүктелуде..." : "Загрузка..."}</p>
            )}
          </div>
        </section>
      </div>

      {showPwdModal && (
        <div
          className="pd-modal-backdrop"
          onClick={(e) => {
            if (e.target.classList.contains("pd-modal-backdrop")) setShowPwdModal(false);
          }}
        >
          <div className="pd-modal pd-modal--sm" role="dialog" aria-modal="true" aria-labelledby="pwd-title-staff">
            <div className="pd-modal-head">
              <h3 id="pwd-title-staff">{locale === "kz" ? "Құпиясөз өзгерту" : "Изменение пароля"}</h3>
              <button type="button" className="pd-close" onClick={() => setShowPwdModal(false)}>
                ×
              </button>
            </div>

            <form onSubmit={handlePasswordChange} className="pd-formgrid pd-formgrid--1c">
              <div className="pd-formgroup">
                <label>{locale === "kz" ? "Қазіргі құпиясөз" : "Текущий пароль"}</label>
                <input
                  type="password"
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                  required
                />
              </div>

              <div className="pd-formgroup">
                <label>{locale === "kz" ? "Жаңа құпиясөз" : "Новый пароль"}</label>
                <input
                  type="password"
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                  required
                />
              </div>

              <div className="pd-formgroup">
                <label>{locale === "kz" ? "Қайта енгізу" : "Повторите пароль"}</label>
                <input
                  type="password"
                  value={passwordData.new_password_confirmation}
                  onChange={(e) =>
                    setPasswordData({ ...passwordData, new_password_confirmation: e.target.value })
                  }
                  required
                />
              </div>

              <div className="pd-row pd-row--tight">
                <button type="submit" className="pd-btn pd-btn--sm">
                  {locale === "kz" ? "Өзгерту" : "Изменить"}
                </button>
                <button
                  type="button"
                  className="pd-btn pd-btn--ghost pd-btn--sm"
                  onClick={() => setShowPwdModal(false)}
                >
                  {locale === "kz" ? "Болдырмау" : "Отмена"}
                </button>
              </div>

              {message && <p className="pd-success">{message}</p>}
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
